import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { ButtonTheme } from 'components/Button';
import { Gap } from 'components/Gap';
import { GapSmall } from 'components/GapSmall';
import { ICONS, Icon } from 'components/Icon';
import { Menu } from 'components/Menu';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { Page } from 'components/Page';
import { PageHeader } from 'components/PageHeader';
import { Pill } from 'components/Pill';
import { ScrollView } from 'components/ScrollView';
import { Txt } from 'components/Txt';
import { useEsc } from 'hooks/useEsc';
import { useSelector } from 'hooks/useSelector';
import { UI_DATE_TIME_FORMAT_LONG } from 'lib/constants';
import { formatDate, rbAddReferencePrefix } from 'lib/riskbase';
import { theme } from 'lib/theme';
import React from 'react';
import { View } from 'react-native';
import { useDispatch } from 'react-redux';
import {
  doPropertyAnalysisDatasetDecisionUpdate,
  doPropertyAnalysisDerivedFieldUpdate,
  NAVIGATE_MODAL_BACK,
} from 'store/actions';
import {
  selectPropertyAnalysisDecisionUpdateError,
  selectPropertyAnalysisDecisionUpdateErrorPropertyId,
  selectPropertyAnalysisDecisionUpdatePropertyId,
  selectPropertyAnalysisDerivedFieldUpdateField,
  selectPropertyAnalysisDerivedFieldUpdateError,
  selectPropertyAnalysisDerivedFieldUpdatePropertyId,
  selectPropertyAnalysisFilterOptions,
  selectPropertyAnalysisItems,
  selectPropertyAnalysisUpdatedProperty,
} from 'store/propertyAnalysis/selectors';
import {
  DatasetDecisionMenu,
  getDatasetDecision,
} from './CssPropertyAnalysisList';
import type {
  CssDatasetDecision,
  CssPropertyAnalysisItem,
  CssResolvableField,
} from 'types/PropertyAnalysis';

const displayValue = (value: string | null): string => value || '-';

const splitValues = (value: string | null): string[] =>
  value
    ?.split(';')
    .map((item) => item.trim())
    .filter(Boolean) ?? [];

const hasCandidateConflict = (value: string | null) =>
  splitValues(value).length > 1;

type DerivedFieldAudit = CssPropertyAnalysisItem['derivedAudit'][string];

const AuditText: React.FC<{ audit?: DerivedFieldAudit }> = ({ audit }) => {
  if (!audit?.changedBy && !audit?.changedAt) {
    return null;
  }

  return (
    <Txt size="xxsmall" style={{ color: theme.colors.textBlack64 }}>
      Last changed by {audit.changedBy ?? 'Unknown'}
      {audit.changedAt &&
        ` (${formatDate(new Date(audit.changedAt), UI_DATE_TIME_FORMAT_LONG)})`}
    </Txt>
  );
};

const RESOLVABLE_VALUES: Record<
  Exclude<CssResolvableField, 'heightMetres'>,
  readonly string[]
> = {
  heightCategory: ['Under 11m', '11-18m', '18+', 'Unknown'],
  buildingAgeBand: [
    'Pre-1980',
    '1980-1990',
    '1991-2010',
    '2011-2026',
    'Unknown',
  ],
  populationDensityBand: [
    '10 or fewer units',
    '11-30 units',
    '31-50 units',
    'Over 50 units',
    'Unknown',
  ],
};

type DerivedResolution = {
  field: CssResolvableField;
  conflict: boolean;
  onChange: (field: CssResolvableField, value: string) => void;
  disabled: boolean;
};

const DerivedResolutionMenu: React.FC<
  DerivedResolution & { value: string | null }
> = ({ field, value, conflict, onChange, disabled }) => {
  const options =
    field === 'heightMetres' ? splitValues(value) : RESOLVABLE_VALUES[field];

  return (
    <Menu
      buttonConfig={{
        backgroundColor: conflict ? theme.colors.yellow : theme.colors.black3,
        buttonTheme: ButtonTheme.themeless,
        chipLike: true,
        disabled: disabled || !options.length,
        iconRight: ICONS.CaretDownSolid,
        iconRightColor: theme.colors.black,
        iconRightSize: 11,
        text: conflict
          ? 'Resolve conflict'
          : splitValues(value).length
            ? 'Change value'
            : 'Set value',
        textSize: 'xsmall',
        textStyle: {
          color: conflict ? theme.colors.black : theme.colors.blue,
        },
        textWeight: 'regular',
      }}
      options={options.map((option) => ({
        key: option,
        leftIcon: option === value ? ICONS.CircleDotSolid : ICONS.CircleLight,
        onPress: () => onChange(field, option),
        text: option,
      }))}
    />
  );
};

const DerivedValues: React.FC<{
  value: string | null;
  reason?: string | null;
  conflictDetails?: string | null;
  resolution?: DerivedResolution;
  audit?: DerivedFieldAudit;
}> = ({ value, reason, conflictDetails, resolution, audit }) => {
  const values = splitValues(value);

  return (
    <View style={{ gap: 4 }}>
      <View
        style={{
          flexDirection: 'row',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 6,
        }}
      >
        {values.length ? (
          values.map((item) => (
            <Pill
              key={item}
              label={item}
              withIcon={false}
              textSize="xsmall"
              style={{ maxWidth: '100%', minHeight: 22 }}
              textStyle={{ flexShrink: 1 }}
            />
          ))
        ) : !resolution ? (
          <Txt>-</Txt>
        ) : null}
        {resolution && <DerivedResolutionMenu value={value} {...resolution} />}
      </View>
      {!!reason && (
        <Txt
          size="xxsmall"
          style={{
            color: theme.colors.textBlack64,
          }}
        >
          <Txt size="xxsmall" weight="medium">
            Reason:{' '}
          </Txt>
          {reason}
        </Txt>
      )}
      {!!conflictDetails && (
        <Txt
          size="xxsmall"
          style={{
            color: theme.colors.textBlack64,
          }}
        >
          <Txt size="xxsmall" weight="medium">
            Conflict details:{' '}
          </Txt>
          {conflictDetails}
        </Txt>
      )}
      <AuditText audit={audit} />
    </View>
  );
};

const StatusValue: React.FC<{
  value: string | null;
  type: 'priority' | 'review' | 'decision';
}> = ({ value, type }) => {
  const normalised = value?.trim().toLowerCase() ?? '';
  const colour =
    type === 'review'
      ? normalised === 'yes'
        ? theme.colors.red
        : normalised === 'no'
          ? theme.colors.green
          : theme.colors.orange
      : type === 'decision'
        ? normalised === 'exclude'
          ? theme.colors.red
          : normalised === 'include'
            ? theme.colors.green
            : theme.colors.orange
        : normalised === 'very high' || normalised === 'high'
          ? theme.colors.red
          : normalised === 'medium'
            ? theme.colors.orange
            : normalised === 'low' || normalised === 'very low'
              ? theme.colors.green
              : theme.colors.black30;

  return (
    <Pill
      label={displayValue(value)}
      withIcon
      icon={ICONS.CircleSolid}
      iconColor={colour}
      textSize="xsmall"
      style={{
        maxWidth: '100%',
        minHeight: 22,
        alignSelf: 'flex-start',
      }}
      textStyle={{ flexShrink: 1 }}
    />
  );
};

const StatusRow: React.FC<{
  label: string;
  value: string | null;
  type: 'priority' | 'review' | 'decision';
  reason?: string | null;
  subText?: string | null;
  audit?: DerivedFieldAudit;
  valueContent?: React.ReactNode;
}> = ({ label, value, type, reason, subText, audit, valueContent }) => (
  <View>
    <Txt weight="medium" style={{ marginHorizontal: theme.padding * 2 }}>
      {label}
    </Txt>
    <Card>
      <CardRow>
        <View style={{ gap: 4, alignItems: 'flex-start' }}>
          {valueContent ?? <StatusValue value={value} type={type} />}
          {!!reason && (
            <Txt
              size="xxsmall"
              style={{
                color: theme.colors.textBlack64,
              }}
            >
              <Txt size="xxsmall" weight="medium">
                Reason:{' '}
              </Txt>
              {reason}
            </Txt>
          )}
          {!!subText && (
            <Txt
              size="xxsmall"
              style={{
                color: theme.colors.textBlack64,
              }}
            >
              {subText}
            </Txt>
          )}
          <AuditText audit={audit} />
        </View>
      </CardRow>
    </Card>
  </View>
);

const DerivedRow: React.FC<{
  label: string;
  value: string | null;
  reason?: string | null;
  conflictDetails?: string | null;
  conflict?: boolean;
  resolution?: DerivedResolution;
  audit?: DerivedFieldAudit;
}> = ({
  label,
  value,
  reason,
  conflictDetails,
  conflict,
  resolution,
  audit,
}) => (
  <View>
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: theme.padding,
        marginHorizontal: theme.padding * 2,
        marginBottom: theme.padding / 2,
      }}
    >
      <Txt weight="medium">{label}</Txt>
      {conflict && (splitValues(value).length > 1 || !!conflictDetails) && (
        <Icon
          source={ICONS.CircleExclamationLight}
          color={theme.colors.orange}
          size={16}
        />
      )}
    </View>
    <Card>
      <CardRow>
        <View style={{ gap: 4, alignItems: 'flex-start' }}>
          <DerivedValues
            value={value}
            reason={reason}
            conflictDetails={conflictDetails}
            resolution={resolution}
            audit={audit}
          />
        </View>
      </CardRow>
    </Card>
  </View>
);

export const CssPropertyAnalysisDetailsModal: React.FC<
  ModalScreenProps<ModalScreens.PropertyAnalysisDetails>
> = ({ route }) => {
  const dispatch = useDispatch();
  const routeProperty = route.params.property;
  const propertyFromList = useSelector(selectPropertyAnalysisItems).find(
    ({ id }) => id === routeProperty.id,
  );
  const updatedProperty = useSelector(selectPropertyAnalysisUpdatedProperty);
  const property =
    propertyFromList ??
    (updatedProperty?.id === routeProperty.id
      ? updatedProperty
      : routeProperty);
  const updatePropertyId = useSelector(
    selectPropertyAnalysisDerivedFieldUpdatePropertyId,
  );
  const updateField = useSelector(
    selectPropertyAnalysisDerivedFieldUpdateField,
  );
  const updateError = useSelector(
    selectPropertyAnalysisDerivedFieldUpdateError,
  );
  const decisionUpdatePropertyId = useSelector(
    selectPropertyAnalysisDecisionUpdatePropertyId,
  );
  const decisionUpdateError = useSelector(
    selectPropertyAnalysisDecisionUpdateError,
  );
  const decisionUpdateErrorPropertyId = useSelector(
    selectPropertyAnalysisDecisionUpdateErrorPropertyId,
  );
  const filterOptions = useSelector(selectPropertyAnalysisFilterOptions);

  const handleDerivedFieldChange = React.useCallback(
    (field: CssResolvableField, value: string) => {
      dispatch(
        doPropertyAnalysisDerivedFieldUpdate({
          propertyId: property.id,
          field,
          value,
        }),
      );
    },
    [dispatch, property.id],
  );

  const handleDatasetDecisionChange = React.useCallback(
    (decision: CssDatasetDecision) => {
      dispatch(
        doPropertyAnalysisDatasetDecisionUpdate({
          propertyId: property.id,
          decision,
        }),
      );
    },
    [dispatch, property.id],
  );

  const getResolution = (field: CssResolvableField, value: string | null) => ({
    field,
    conflict: hasCandidateConflict(value),
    onChange: handleDerivedFieldChange,
    disabled: updatePropertyId === property.id && updateField === field,
  });

  const handleClose = React.useCallback(() => {
    dispatch(NAVIGATE_MODAL_BACK());
  }, [dispatch]);

  useEsc(handleClose);

  return (
    <Page
      header={
        <PageHeader
          title={property.name}
          subtitle="Property Analysis"
          onPressCancelButton={handleClose}
        />
      }
    >
      <ScrollView>
        <Gap>
          <GapSmall>
            <Card>
              <CardRow
                label={property.name}
                value={displayValue(property.address)}
                labelAlignTop
                labelNumberOfLines={0}
                valueNumberOfLines={0}
              />
              <View style={{ flexDirection: 'row' }}>
                <CardRow
                  value={rbAddReferencePrefix(property.reference)}
                  valueNumberOfLines={1}
                  icon={ICONS.RBPlectrumSolid}
                />
                <CardRow
                  value={displayValue(property.customReference)}
                  valueNumberOfLines={1}
                  icon={ICONS.HashtagLight}
                  style={{ flex: 1 }}
                />
              </View>
            </Card>
          </GapSmall>

          <View style={{ gap: theme.padding / 2 }}>
            <GapSmall>
              <StatusRow
                label="Screening priority"
                value={property.derived.screeningPriority}
                type="priority"
                reason={property.derived.screeningPriorityReason}
                audit={property.derivedAudit.screeningPriority}
              />
              <StatusRow
                label="Dataset decision"
                value={property.derived.datasetDecision}
                type="decision"
                audit={property.derivedAudit.datasetDecision}
                valueContent={
                  <DatasetDecisionMenu
                    value={getDatasetDecision(property.derived.datasetDecision)}
                    options={filterOptions.datasetDecisions}
                    onChange={handleDatasetDecisionChange}
                    disabled={decisionUpdatePropertyId === property.id}
                    error={
                      decisionUpdateErrorPropertyId === property.id
                        ? decisionUpdateError
                        : null
                    }
                  />
                }
              />
              <StatusRow
                label="Conflicting evidence"
                value={property.derived.reviewRequired}
                type="review"
                reason={property.derived.reviewRequiredReason}
                audit={property.derivedAudit.reviewRequired}
              />
            </GapSmall>
          </View>

          <View style={{ gap: theme.padding / 2 }}>
            <Txt
              weight="medium"
              style={{ marginHorizontal: theme.padding * 2 }}
            >
              Derived property attributes
            </Txt>
            {!!updateError && (
              <Txt
                size="xxsmall"
                style={{
                  color: theme.colors.red,
                  marginHorizontal: theme.padding * 2,
                }}
              >
                {updateError}
              </Txt>
            )}
            <GapSmall>
              <DerivedRow
                label="Height category"
                value={property.derived.heightCategory}
                reason={property.derived.heightCategoryReason}
                conflictDetails={property.conflicts.heightConflictDetails}
                conflict
                audit={property.derivedAudit.heightCategory}
                resolution={getResolution(
                  'heightCategory',
                  property.derived.heightCategory,
                )}
              />
              <DerivedRow
                label="Height"
                value={property.derived.heightMetres}
                reason={property.derived.heightMetresReason}
                conflictDetails={property.conflicts.heightConflictDetails}
                conflict
                audit={property.derivedAudit.heightMetres}
                resolution={
                  hasCandidateConflict(property.derived.heightMetres)
                    ? getResolution(
                        'heightMetres',
                        property.derived.heightMetres,
                      )
                    : undefined
                }
              />
              <DerivedRow
                label="External wall construction"
                value={property.derived.externalWallConstruction}
                reason={property.derived.externalWallConstructionReason}
                conflictDetails={property.conflicts.externalWallConflictDetails}
                conflict={!!property.conflicts.externalWallConflictDetails}
                audit={property.derivedAudit.externalWallConstruction}
              />
              <DerivedRow
                label="External wall risk indicators"
                value={property.derived.externalWallRiskIndicators}
                reason={property.derived.externalWallRiskIndicatorsReason}
                conflictDetails={property.conflicts.externalWallConflictDetails}
                conflict={!!property.conflicts.externalWallConflictDetails}
                audit={property.derivedAudit.externalWallRiskIndicators}
              />
              <DerivedRow
                label="Building age band"
                value={property.derived.buildingAgeBand}
                reason={property.derived.buildingAgeBandReason}
                conflictDetails={property.conflicts.ageConflictDetails}
                conflict
                audit={property.derivedAudit.buildingAgeBand}
                resolution={getResolution(
                  'buildingAgeBand',
                  property.derived.buildingAgeBand,
                )}
              />
              <DerivedRow
                label="Population density band"
                value={property.derived.populationDensityBand}
                reason={property.derived.populationDensityBandReason}
                conflict
                audit={property.derivedAudit.populationDensityBand}
                resolution={getResolution(
                  'populationDensityBand',
                  property.derived.populationDensityBand,
                )}
              />
            </GapSmall>
          </View>
        </Gap>
      </ScrollView>
    </Page>
  );
};
